import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { McpAcknowledgeDialogComponent } from '../../../components/dialogs/mcp-acknowledge-dialog/mcp-acknowledge-dialog.component';
import { McpConnectorDialogComponent } from '../../../components/dialogs/mcp-connector-dialog/mcp-connector-dialog.component';
import {
  MCP_AUTO_DISABLE_AFTER_FAILURES,
  McpConnector,
  McpDryRunResult,
  McpService,
  McpTool,
} from '../../../services/mcp.service';

/**
 * One connector: review every tool before an agent can use it.
 *
 * The model sees the APPROVED description (`description_effective`) and
 * schema, never the server's live text. When the server changes a tool, the
 * page shows the difference and offers to take the new version over — which
 * then needs a new acknowledgement, like enabling a tool or editing a
 * description does. The backend enforces that (it switches the connector off);
 * the page only explains it.
 */
@Component({
  selector: 'app-mcp-connector-detail',
  templateUrl: './mcp-connector-detail.component.html',
  styleUrl: './mcp-connector-detail.component.scss',
})
export class McpConnectorDetailComponent implements OnInit {
  connector: McpConnector | null = null;
  loading = false;
  busy = false;

  /** per tool: edited description, dry-run arguments and result */
  descDraft: Record<string, string> = {};
  argsDraft: Record<string, string> = {};
  dryResult: Record<string, McpDryRunResult | { error: string }> = {};
  dryRunning: Record<string, boolean> = {};

  readonly autoDisableAfter = MCP_AUTO_DISABLE_AFTER_FAILURES;

  private connectorId = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private mcp: McpService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.connectorId = this.route.snapshot.paramMap.get('connectorId') || '';
    this.load();
  }

  load(): void {
    this.loading = true;
    this.mcp.get(this.connectorId).subscribe({
      next: (c) => {
        this.loading = false;
        if (!c) {
          this.router.navigate(['/agent-setup/mcp']);
          return;
        }
        this.apply(c);
      },
      error: () => {
        this.loading = false;
        this.toastr.danger(this.t.instant('pages.mcp.errors.loadFailed'), '');
      },
    });
  }

  private apply(c: McpConnector): void {
    this.connector = c;
    for (const tool of c.tools) {
      this.descDraft[tool.id] = tool.description_effective || '';
      if (this.argsDraft[tool.id] === undefined) {
        this.argsDraft[tool.id] = JSON.stringify(this.exampleArgs(tool), null, 2);
      }
    }
  }

  get enabledCount(): number {
    return this.connector ? this.connector.tools.filter((t) => t.is_enabled && !t.removed).length : 0;
  }

  get autoDisabled(): boolean {
    return !!this.connector && !this.connector.is_active && this.connector.consecutive_failures >= this.autoDisableAfter;
  }

  descDirty(tool: McpTool): boolean {
    return (this.descDraft[tool.id] || '').trim() !== (tool.description_effective || '').trim();
  }

  schemaText(tool: McpTool): string {
    return JSON.stringify(tool.input_schema || {}, null, 2);
  }

  // ── actions ──

  private run(req$: Observable<McpConnector>, successKey?: string): void {
    this.busy = true;
    req$.subscribe({
      next: (c) => {
        this.busy = false;
        const wasActive = this.connector?.is_active;
        this.apply(c);
        if (successKey) this.toastr.success(this.t.instant(successKey), '');
        if (wasActive && !c.is_active) {
          this.toastr.warning(this.t.instant('pages.mcp.detail.deactivatedByChange'), '');
        }
      },
      error: (err) => {
        this.busy = false;
        const detail = err?.error?.detail;
        this.toastr.danger(typeof detail === 'string' ? detail : this.t.instant('pages.mcp.errors.saveFailed'), '');
        // The server's truth — e.g. a toggle already flipped in the model.
        this.load();
      },
    });
  }

  get availableCount(): number {
    return this.connector ? this.connector.tools.filter((t) => !t.removed).length : 0;
  }

  setAllTools(enabled: boolean): void {
    this.run(
      this.mcp.setAllTools(this.connectorId, enabled),
      enabled ? 'pages.mcp.detail.allEnabled' : 'pages.mcp.detail.allDisabled'
    );
  }

  toggleTool(tool: McpTool, enabled: boolean): void {
    // Mirror the flip into the model first (CLAUDE.md rule 17), so a failed
    // request can be reverted to what the server says.
    tool.is_enabled = enabled;
    this.run(this.mcp.updateTool(this.connectorId, tool.id, { is_enabled: enabled }));
  }

  saveDescription(tool: McpTool): void {
    this.run(
      this.mcp.updateTool(this.connectorId, tool.id, { description_effective: (this.descDraft[tool.id] || '').trim() }),
      'pages.mcp.detail.descriptionSaved'
    );
  }

  acceptServerVersion(tool: McpTool): void {
    this.run(this.mcp.updateTool(this.connectorId, tool.id, { accept_server_version: true }), 'pages.mcp.detail.serverVersionTaken');
  }

  rediscover(): void {
    this.run(this.mcp.discover(this.connectorId), 'pages.mcp.detail.rediscovered');
  }

  activate(): void {
    this.run(this.mcp.activate(this.connectorId), 'pages.mcp.detail.activated');
  }

  deactivate(): void {
    this.run(this.mcp.deactivate(this.connectorId), 'pages.mcp.detail.deactivated');
  }

  openAcknowledge(): void {
    if (!this.connector) return;
    this.dialogService.open(McpAcknowledgeDialogComponent, {
      context: {
        connector: this.connector,
        onDone: (c: McpConnector) => {
          this.apply(c);
          this.toastr.success(this.t.instant('pages.mcp.ack.done'), '');
        },
      },
    });
  }

  editConnection(): void {
    if (!this.connector) return;
    this.dialogService.open(McpConnectorDialogComponent, {
      context: {
        connector: this.connector,
        onSaved: (c: McpConnector) => {
          const wasActive = this.connector?.is_active;
          this.apply(c);
          this.toastr.success(this.t.instant('pages.mcp.detail.connectionSaved'), '');
          if (wasActive && !c.is_active) {
            this.toastr.warning(this.t.instant('pages.mcp.detail.deactivatedByChange'), '');
          }
        },
      },
    });
  }

  dryRun(tool: McpTool): void {
    let args: Record<string, any>;
    try {
      args = JSON.parse(this.argsDraft[tool.id] || '{}');
      if (typeof args !== 'object' || Array.isArray(args) || args === null) throw new Error('not an object');
    } catch {
      this.dryResult[tool.id] = { error: this.t.instant('pages.mcp.detail.argsInvalid') };
      return;
    }
    this.dryRunning[tool.id] = true;
    this.mcp.dryRun(this.connectorId, tool.tool_name, args).subscribe({
      next: (res) => {
        this.dryRunning[tool.id] = false;
        this.dryResult[tool.id] = res;
      },
      error: (err) => {
        this.dryRunning[tool.id] = false;
        const detail = err?.error?.detail;
        this.dryResult[tool.id] = {
          error: typeof detail === 'string' ? detail : this.t.instant('pages.mcp.errors.saveFailed'),
        };
      },
    });
  }

  isError(res: McpDryRunResult | { error: string } | undefined): res is { error: string } {
    return !!res && 'error' in res;
  }

  trackTool = (_: number, tool: McpTool) => tool.id;

  /** A starting point for the dry run: required properties with empty
   *  values of the right type. */
  private exampleArgs(tool: McpTool): Record<string, any> {
    const props = tool.input_schema?.['properties'] || {};
    const out: Record<string, any> = {};
    for (const key of tool.input_schema?.['required'] || []) {
      const type = props[key]?.type;
      out[key] = type === 'number' || type === 'integer' ? 0 : type === 'boolean' ? false : type === 'array' ? [] : '';
    }
    return out;
  }
}
